import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Page,
  Layout,
  Card,
  FormLayout,
  TextField,
  Select,
  Button,
  Text,
  BlockStack,
  InlineStack,
  Banner,
  DataTable,
  Link,
  Tooltip,
  Modal,
  Spinner,
  Badge,
  Box,
  Divider,
  EmptyState,
} from '@shopify/polaris';
import { ClipboardIcon, CodeIcon } from '@shopify/polaris-icons';
import {
  useShopifyFetch,
  ApiError,
  ProductsResponse,
  GeneratedLink,
  LinksResponse,
} from '../hooks/useShopifyFetch';
import { useToast } from '../context/ToastContext';

export default function Links() {
  const { get, post } = useShopifyFetch();
  const navigate = useNavigate();
  const { showToast } = useToast();

  // State
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [customUrl, setCustomUrl] = useState('');
  const [linkName, setLinkName] = useState('');
  const [generatedLink, setGeneratedLink] = useState<GeneratedLink | null>(null);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Fetch products for dropdown
  const { data: productsData } = useQuery<ProductsResponse>({
    queryKey: ['products', 'all'],
    queryFn: () => get('/api/products?limit=250'),
  });

  const products = productsData?.products || [];

  // Generate link mutation
  const generateMutation = useMutation({
    mutationFn: (data: {
      product_id?: string;
      destination_url?: string;
      link_name?: string;
    }) => post<GeneratedLink>('/api/links/generate', data),
    onSuccess: (data) => {
      setGeneratedLink(data);
    },
    onError: (error) => {
      // Backend hard-blocks over-quota link generation with HTTP 402 +
      // detail { code: "plan_limit_reached", limit, plan, upgrade_url }.
      if (error instanceof ApiError && error.status === 402) {
        showToast(
          "You've reached your plan's affiliate limit. Upgrade to generate more links.",
          { error: true }
        );
        // Surface the upgrade path (Billing has the upgrade modal/plans).
        navigate(`/billing${window.location.search}`);
        return;
      }
      showToast(
        error instanceof Error ? error.message : 'Failed to generate link. Please try again.',
        { error: true }
      );
    },
  });

  // Fetch existing links
  const { data: linksData } = useQuery<LinksResponse>({
    queryKey: ['links'],
    queryFn: () => get('/api/links'),
  });

  // Handlers
  const handleGenerateLink = useCallback(() => {
    if (selectedProduct) {
      generateMutation.mutate({ product_id: selectedProduct });
    } else if (customUrl) {
      generateMutation.mutate({
        destination_url: customUrl,
        link_name: linkName || undefined,
      });
    }
  }, [selectedProduct, customUrl, linkName, generateMutation]);

  const handleCopy = useCallback(async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  const handleReset = useCallback(() => {
    setSelectedProduct('');
    setCustomUrl('');
    setLinkName('');
    setGeneratedLink(null);
  }, []);

  // Product options for select
  const productOptions = [
    { label: 'Select a product', value: '' },
    ...products.map((p) => ({
      label: p.title,
      value: p.id,
    })),
  ];

  return (
    <Page title="Affiliate Links" subtitle="Generate trackable affiliate links for your products">
      <Layout>
        {/* Link Generator */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">
                Generate New Link
              </Text>

              <FormLayout>
                <Select
                  label="Select Product"
                  options={productOptions}
                  value={selectedProduct}
                  onChange={(value) => {
                    setSelectedProduct(value);
                    setCustomUrl('');
                    setGeneratedLink(null);
                  }}
                  disabled={!!customUrl}
                />

                <Text variant="bodySm" tone="subdued" as="p" alignment="center">
                  — OR —
                </Text>

                <TextField
                  label="Custom URL"
                  value={customUrl}
                  onChange={(value) => {
                    setCustomUrl(value);
                    setSelectedProduct('');
                    setGeneratedLink(null);
                  }}
                  placeholder="https://yourstore.myshopify.com/collections/summer"
                  disabled={!!selectedProduct}
                  autoComplete="off"
                />

                {customUrl && (
                  <TextField
                    label="Link Name (optional)"
                    value={linkName}
                    onChange={setLinkName}
                    placeholder="Summer Collection"
                    autoComplete="off"
                  />
                )}

                <InlineStack gap="300">
                  <Button
                    variant="primary"
                    onClick={handleGenerateLink}
                    loading={generateMutation.isPending}
                    disabled={!selectedProduct && !customUrl}
                  >
                    Generate Link
                  </Button>
                  {(selectedProduct || customUrl || generatedLink) && (
                    <Button onClick={handleReset}>Reset</Button>
                  )}
                </InlineStack>
              </FormLayout>

              {generateMutation.isError &&
                !(
                  generateMutation.error instanceof ApiError &&
                  generateMutation.error.status === 402
                ) && (
                  <Banner tone="critical">
                    <p>Failed to generate link. Make sure you're connected to Affilync.</p>
                  </Banner>
                )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Generated Link Result */}
        {generatedLink && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                  <Text variant="headingMd" as="h2">
                    Generated Link
                  </Text>
                  <Badge tone="success">Ready to Share</Badge>
                </InlineStack>

                {generatedLink.product && (
                  <Box
                    background="bg-surface-secondary"
                    padding="300"
                    borderRadius="200"
                  >
                    <BlockStack gap="100">
                      <Text variant="bodySm" tone="subdued" as="p">
                        Product
                      </Text>
                      <Text variant="bodyMd" as="p" fontWeight="semibold">
                        {generatedLink.product.title}
                      </Text>
                    </BlockStack>
                  </Box>
                )}

                <Divider />

                {/* Affiliate URL */}
                <BlockStack gap="200">
                  <Text variant="bodySm" tone="subdued" as="p">
                    Affiliate URL
                  </Text>
                  <InlineStack gap="200" blockAlign="center">
                    <Box width="100%">
                      <TextField
                        label=""
                        labelHidden
                        value={generatedLink.affiliate_url}
                        readOnly
                        autoComplete="off"
                      />
                    </Box>
                    <Tooltip content={copiedField === 'affiliate' ? 'Copied!' : 'Copy'}>
                      <Button
                        icon={ClipboardIcon}
                        onClick={() => handleCopy(generatedLink.affiliate_url, 'affiliate')}
                        accessibilityLabel="Copy affiliate URL"
                      />
                    </Tooltip>
                  </InlineStack>
                </BlockStack>

                {/* Short URL */}
                {generatedLink.short_url && (
                  <BlockStack gap="200">
                    <Text variant="bodySm" tone="subdued" as="p">
                      Short URL
                    </Text>
                    <InlineStack gap="200" blockAlign="center">
                      <Box width="100%">
                        <TextField
                          label=""
                          labelHidden
                          value={generatedLink.short_url}
                          readOnly
                          autoComplete="off"
                        />
                      </Box>
                      <Tooltip content={copiedField === 'short' ? 'Copied!' : 'Copy'}>
                        <Button
                          icon={ClipboardIcon}
                          onClick={() => handleCopy(generatedLink.short_url, 'short')}
                          accessibilityLabel="Copy short URL"
                        />
                      </Tooltip>
                    </InlineStack>
                  </BlockStack>
                )}

                {/* Tracking Code */}
                <BlockStack gap="200">
                  <Text variant="bodySm" tone="subdued" as="p">
                    Tracking Code
                  </Text>
                  <InlineStack gap="200" blockAlign="center">
                    <Box width="100%">
                      <TextField
                        label=""
                        labelHidden
                        value={generatedLink.tracking_code}
                        readOnly
                        autoComplete="off"
                      />
                    </Box>
                    <Tooltip content={copiedField === 'tracking' ? 'Copied!' : 'Copy'}>
                      <Button
                        icon={ClipboardIcon}
                        onClick={() => handleCopy(generatedLink.tracking_code, 'tracking')}
                        accessibilityLabel="Copy tracking code"
                      />
                    </Tooltip>
                  </InlineStack>
                </BlockStack>

                {/* QR Code */}
                {generatedLink.qr_code_url && (
                  <InlineStack gap="300">
                    <Button icon={CodeIcon} onClick={() => setQrModalOpen(true)}>
                      View QR Code
                    </Button>
                  </InlineStack>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Recent Links */}
        {linksData && (!linksData.links || linksData.links.length === 0) && (
          <Layout.Section>
            <Card>
              <EmptyState
                heading="No affiliate links yet"
                action={{
                  content: 'Import products',
                  onAction: () => navigate(`/products${window.location.search}`),
                }}
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>
                  Generate a trackable link above from one of your products, or import
                  products first to build your affiliate catalog.
                </p>
              </EmptyState>
            </Card>
          </Layout.Section>
        )}

        {linksData?.links && linksData.links.length > 0 && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">
                  Recent Links
                </Text>
                <DataTable
                  columnContentTypes={['text', 'text', 'numeric', 'numeric']}
                  headings={['Link', 'Created', 'Clicks', 'Conversions']}
                  rows={linksData.links.slice(0, 10).map((link: any) => [
                    <Link url={link.affiliate_url} external key={link.id}>
                      {link.name || link.short_url || 'Affiliate Link'}
                    </Link>,
                    new Date(link.created_at).toLocaleDateString(),
                    link.clicks || 0,
                    link.conversions || 0,
                  ])}
                />
              </BlockStack>
            </Card>
          </Layout.Section>
        )}
      </Layout>

      {/* QR Code Modal */}
      <Modal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        title="QR Code"
        primaryAction={{
          content: 'Download',
          onAction: () => {
            if (generatedLink?.qr_code_url) {
              window.open(generatedLink.qr_code_url, '_blank');
            }
          },
        }}
        secondaryActions={[
          {
            content: 'Close',
            onAction: () => setQrModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400" inlineAlign="center">
            {generatedLink?.qr_code_url ? (
              <img
                src={generatedLink.qr_code_url}
                alt="QR Code"
                style={{ maxWidth: '200px', maxHeight: '200px' }}
              />
            ) : (
              <Spinner />
            )}
            <Text variant="bodySm" tone="subdued" as="p" alignment="center">
              Scan this QR code to open the affiliate link
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
